import type { ComponentType, ReactNode, SVGProps } from 'react';
import { cn } from '../../../lib/utils.js';

/**
 * Headline dashboard widget. Mirrors the legacy
 * `wf-audit/src/slices/dashboard/components/dashboard/widgets/*Widget.tsx`
 * shape: tinted icon on the left, label on top, big number + caption
 * stack on the right. Caller passes the icon component (lucide) and
 * the tint so we don't keep a `color → bg/icon-tone` map on this end.
 */
export interface StatCardProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  /** Tailwind colour-name for the icon tile + glyph. Defaults to amber. */
  tint?: 'amber' | 'cyan' | 'rose' | 'emerald' | 'violet' | 'sky' | 'orange';
  className?: string;
}

const TINTS: Record<NonNullable<StatCardProps['tint']>, { bg: string; fg: string }> = {
  amber: { bg: 'bg-amber-500/15', fg: 'text-amber-700 dark:text-amber-300' },
  cyan: { bg: 'bg-cyan-500/15', fg: 'text-cyan-700 dark:text-cyan-300' },
  rose: { bg: 'bg-rose-500/15', fg: 'text-rose-700 dark:text-rose-300' },
  emerald: { bg: 'bg-emerald-500/15', fg: 'text-emerald-700 dark:text-emerald-300' },
  violet: { bg: 'bg-violet-500/15', fg: 'text-violet-700 dark:text-violet-300' },
  sky: { bg: 'bg-sky-500/15', fg: 'text-sky-700 dark:text-sky-300' },
  orange: { bg: 'bg-orange-500/15', fg: 'text-orange-700 dark:text-orange-300' },
};

export function StatCard({
  icon: Icon,
  label,
  value,
  caption,
  tint = 'amber',
  className,
}: StatCardProps): JSX.Element {
  const t = TINTS[tint];
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.03] p-4',
        className,
      )}
    >
      <div className={cn('flex h-10 w-10 items-center justify-center rounded-md', t.bg)}>
        <Icon className={cn('h-5 w-5', t.fg)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-foreground/50">{label}</div>
        <div className="mt-0.5 truncate text-2xl font-semibold text-foreground">{value}</div>
        {caption ? <div className="text-xs text-foreground/50">{caption}</div> : null}
      </div>
    </div>
  );
}
